// src/pages/customer-panel/appointments/AppointmentsPage.js
// Customer Appointments - List, Filter, Search, Request

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    Snackbar,
    InputAdornment,
    Tabs,
    Tab,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '../../../utils/axiosInstance';
import PaginationBar from '../components/PaginationBar';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';

const STATUS_CONFIG = {
    PENDING:   { color: '#f59e0b', bg: '#fef3c7', label: 'Pending' },
    CONFIRMED: { color: '#0ea5e9', bg: '#e0f2fe', label: 'Confirmed' },
    IN_PROGRESS: { color: '#10b981', bg: '#d1fae5', label: 'In Progress' },
    COMPLETED: { color: '#6366f1', bg: '#e0e7ff', label: 'Completed' },
    BILLED:    { color: '#8b5cf6', bg: '#ede9fe', label: 'Billed' },
    CLOSED:    { color: '#059669', bg: '#d1fae5', label: 'Closed' },
    CANCELLED: { color: '#ef4444', bg: '#fee2e2', label: 'Cancelled' },
    NO_SHOW:   { color: '#94a3b8', bg: '#f1f5f9', label: 'No Show' },
};

const STATUS_TABS = ['all', 'PENDING', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'BILLED', 'CLOSED', 'CANCELLED', 'NO_SHOW'];
const STATUS_TAB_LABELS = ['All', 'Pending', 'Confirmed', 'In Progress', 'Completed', 'Billed', 'Closed', 'Cancelled', 'No Show'];

const INITIAL_FORM = {
    title: '',
    description: '',
    appointment_date: '',
    appointment_time: '',
    duration_minutes: 30,
    location: '',
    notes: '',
};

// --- Helper: Debounce Hook ---
function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

// --- Helper: Format Date ---
function formatDate(dateStr) {
    if (!dateStr) return '--';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return '--';
    }
}

function formatTime(timeStr) {
    if (!timeStr) return '--';
    // Return HH:MM in 24-hour format
    const parts = timeStr.split(':');
    if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    return timeStr;
}

// --- Status Chip ---
function StatusChip({ status }) {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    return (
        <Chip
            label={config.label}
            size="small"
            sx={{
                fontFamily,
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: config.bg,
                color: config.color,
            }}
        />
    );
}

// --- Shared Appointment Form Dialog (Settings-Aware) ---
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' };

function AppointmentFormDialog({ open, onClose, onSubmit, mode = 'request', customers = [], loadingCustomers = false }) {
    const [formData, setFormData] = useState({ ...INITIAL_FORM, customer_user_id: '', admin_override: false });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [settings, setSettings] = useState(null);
    const [loadingSettings, setLoadingSettings] = useState(true);
    const [dateWarning, setDateWarning] = useState('');
    const [availableHours, setAvailableHours] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);

    useEffect(() => {
        if (open) {
            setFormData({ ...INITIAL_FORM, customer_user_id: '', admin_override: false });
            setError('');
            setDateWarning('');
            setAvailableHours(null);
            setAvailableSlots([]);
            setSelectedSlot(null);
            fetchSettings();
        }
    }, [open]);

    const fetchSettings = async () => {
        setLoadingSettings(true);
        try {
            const res = await axiosInstance.get('/api/appointment-settings');
            const data = res.data?.data;
            if (data && data.id) {
                setSettings(data);
                if (data.default_duration_minutes) {
                    setFormData(prev => ({ ...prev, duration_minutes: data.default_duration_minutes }));
                }
            }
        } catch { /* ignore */ }
        finally { setLoadingSettings(false); }
    };

    // When date changes, check availability
    useEffect(() => {
        if (!formData.appointment_date || !settings) {
            setDateWarning('');
            setAvailableHours(null);
            return;
        }
        const selDate = new Date(formData.appointment_date + 'T00:00:00');
        const dayKey = DAY_KEYS[selDate.getDay() === 0 ? 6 : selDate.getDay() - 1];
        const dateStr = formData.appointment_date;

        // Check holiday
        const holidays = settings.holidays || [];
        for (const h of holidays) {
            if (!h.date) continue;
            if (h.recurring) {
                const hd = h.date.substring(5); // MM-DD
                const sd = dateStr.substring(5);
                if (hd === sd) { setDateWarning(`This date is a holiday: ${h.name}`); setAvailableHours(null); return; }
            } else {
                if (h.date === dateStr) { setDateWarning(`This date is a holiday: ${h.name}`); setAvailableHours(null); return; }
            }
        }

        // Check special day
        const specialDays = settings.special_days || [];
        const special = specialDays.find(sd => sd.date === dateStr);
        if (special) {
            setDateWarning('');
            setAvailableHours({ start: special.start, end: special.end, label: `Special hours (${special.name}): ${special.start} - ${special.end}` });
            return;
        }

        // Check weekly availability
        const weeklyAvail = settings.weekly_availability || {};
        const daySchedule = weeklyAvail[dayKey];
        if (!daySchedule || !daySchedule.enabled) {
            setDateWarning(`${DAY_LABELS[dayKey]}s are not available for appointments`);
            setAvailableHours(null);
            return;
        }

        setDateWarning('');
        setAvailableHours({ start: daySchedule.start, end: daySchedule.end, label: `Available: ${daySchedule.start} - ${daySchedule.end}` });
    }, [formData.appointment_date, settings]);

    // Fetch available slots when date changes
    useEffect(() => {
        if (!formData.appointment_date || dateWarning) {
            setAvailableSlots([]);
            setSelectedSlot(null);
            return;
        }
        setLoadingSlots(true);
        setSelectedSlot(null);
        setFormData(prev => ({ ...prev, appointment_time: '' }));
        axiosInstance.get(`/api/appointment-slots/available?date=${formData.appointment_date}`)
            .then(res => {
                if (res.data?.success) {
                    setAvailableSlots(res.data.data || []);
                    // Auto-set duration from slot settings
                    if (res.data.slot_duration_minutes) {
                        setFormData(prev => ({ ...prev, duration_minutes: res.data.slot_duration_minutes }));
                    }
                } else {
                    setAvailableSlots([]);
                }
            })
            .catch(() => setAvailableSlots([]))
            .finally(() => setLoadingSlots(false));
    }, [formData.appointment_date, dateWarning]);

    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async () => {
        if (!formData.title.trim() || !formData.appointment_date || !formData.appointment_time) {
            setError('Title, date, and time are required.');
            return;
        }
        if (mode === 'admin' && !formData.customer_user_id) {
            setError('Please select a customer.');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                appointment_date: formData.appointment_date,
                appointment_time: formData.appointment_time,
                duration_minutes: formData.duration_minutes || 30,
                location: formData.location,
                notes: formData.notes,
            };
            if (mode === 'admin') {
                payload.customer_user_id = formData.customer_user_id;
                payload.admin_override = formData.admin_override;
            }
            await onSubmit(payload);
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to submit appointment.');
        } finally {
            setSubmitting(false);
        }
    };

    const inputSx = {
        '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' },
        '& .MuiInputLabel-root': { fontFamily },
    };

    // Compute min/max dates as Date objects for DatePicker
    const getMinDate = () => {
        const now = new Date();
        if (settings?.min_notice_hours) now.setHours(now.getHours() + settings.min_notice_hours);
        return now;
    };

    const getMaxDate = () => {
        const max = new Date();
        max.setDate(max.getDate() + (settings?.max_days_ahead || 60));
        return max;
    };

    // Disable unavailable dates in DatePicker (holidays + disabled weekdays)
    const shouldDisableDate = (dateVal) => {
        if (!settings || !dateVal) return false;
        const d = new Date(dateVal);
        const dayKey = DAY_KEYS[d.getDay() === 0 ? 6 : d.getDay() - 1];

        // Check if day of week is disabled
        const avail = settings.weekly_availability || {};
        if (avail[dayKey] && !avail[dayKey].enabled) return true;

        // Check holidays
        const dateStr = d.toISOString().split('T')[0];
        for (const h of (settings.holidays || [])) {
            if (!h.date) continue;
            if (h.recurring) {
                if (h.date.substring(5) === dateStr.substring(5)) return true;
            } else {
                if (h.date === dateStr) return true;
            }
        }
        return false;
    };

    // Build available days info
    const getAvailableDaysSummary = () => {
        if (!settings?.weekly_availability) return null;
        const avail = settings.weekly_availability;
        const days = DAY_KEYS.filter(d => avail[d]?.enabled).map(d => DAY_LABELS[d].substring(0, 3));
        return days.join(', ');
    };

    // Build upcoming holidays info
    const getUpcomingHolidays = () => {
        if (!settings?.holidays?.length) return [];
        const today = new Date().toISOString().split('T')[0];
        return settings.holidays.filter(h => {
            if (h.recurring) return true;
            return h.date >= today;
        }).slice(0, 5);
    };

    const isAdmin = mode === 'admin';
    const title = isAdmin ? 'Create Appointment' : 'Request Appointment';
    const submitLabel = isAdmin ? 'Create' : 'Submit Request';
    const availDays = getAvailableDaysSummary();
    const upcomingHolidays = getUpcomingHolidays();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {title}
                <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5, color: '#94a3b8' }}><CloseIcon /></Button>
            </DialogTitle>
            <DialogContent>
                {loadingSettings ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: GREEN_ACCENT }} /></Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 3, pt: 1, flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* LEFT: Form */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {error && <Alert severity="error" sx={{ fontFamily, borderRadius: '10px' }}>{error}</Alert>}

                            {isAdmin && (
                                <FormControl fullWidth sx={inputSx}>
                                    <InputLabel sx={{ fontFamily }}>Customer *</InputLabel>
                                    <Select value={formData.customer_user_id} label="Customer *" onChange={handleChange('customer_user_id')} disabled={loadingCustomers} sx={{ fontFamily, borderRadius: '10px' }}>
                                        {loadingCustomers && <MenuItem value="" sx={{ fontFamily }}>Loading...</MenuItem>}
                                        {customers.map((c) => (
                                            <MenuItem key={c.user_id} value={c.user_id} sx={{ fontFamily }}>
                                                {c.profile ? `${c.profile.fname || ''} ${c.profile.lname || ''}`.trim() : c.user_email}
                                                {c.is_crm_customer && <Chip label="CRM" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#fef3c7', color: '#d97706' }} />}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <TextField label="Title *" fullWidth value={formData.title} onChange={handleChange('title')} sx={inputSx} />
                            <TextField label="Description" fullWidth multiline rows={2} value={formData.description} onChange={handleChange('description')} sx={inputSx} />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Date *"
                                    value={formData.appointment_date ? new Date(formData.appointment_date + 'T00:00:00') : null}
                                    onChange={(newVal) => {
                                        if (newVal) {
                                            const yyyy = newVal.getFullYear();
                                            const mm = String(newVal.getMonth() + 1).padStart(2, '0');
                                            const dd = String(newVal.getDate()).padStart(2, '0');
                                            setFormData(prev => ({ ...prev, appointment_date: `${yyyy}-${mm}-${dd}` }));
                                        } else {
                                            setFormData(prev => ({ ...prev, appointment_date: '' }));
                                        }
                                    }}
                                    minDate={getMinDate()}
                                    maxDate={getMaxDate()}
                                    shouldDisableDate={shouldDisableDate}
                                    renderInput={(params) => (
                                        <TextField {...params} fullWidth sx={inputSx} />
                                    )}
                                />
                            </LocalizationProvider>

                            {dateWarning && <Alert severity="error" sx={{ fontFamily, borderRadius: '10px', py: 0.5 }}>{dateWarning}</Alert>}
                            {!dateWarning && availableHours && (
                                <Alert severity="info" sx={{ fontFamily, borderRadius: '10px', py: 0.5 }}>{availableHours.label}</Alert>
                            )}

                            {/* Slot Selection */}
                            {formData.appointment_date && !dateWarning && (
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, mb: 1, color: '#334155' }}>
                                        Select a Time Slot *
                                    </Typography>
                                    {loadingSlots ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size={24} sx={{ color: GREEN_ACCENT }} />
                                        </Box>
                                    ) : availableSlots.length === 0 ? (
                                        <Alert severity="info" sx={{ fontFamily, borderRadius: '10px' }}>
                                            No available slots for this date. Please try another date.
                                        </Alert>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {availableSlots.map((slot) => {
                                                const slotKey = slot.start || slot.time;
                                                const slotLabel = slot.end ? `${slot.start} - ${slot.end}` : slot.start;
                                                const isSelected = selectedSlot === slotKey;
                                                const isFull = !slot.available;
                                                return (
                                                    <Chip
                                                        key={slotKey}
                                                        label={
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                                                                <span>{slotLabel}</span>
                                                                {isFull && <span style={{ fontSize: '0.6rem', opacity: 0.8 }}>Full</span>}
                                                                {!isFull && slot.max_bookings > 1 && <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>{slot.booked_count}/{slot.max_bookings}</span>}
                                                            </Box>
                                                        }
                                                        clickable={!isFull}
                                                        disabled={isFull}
                                                        onClick={() => {
                                                            if (!isFull) {
                                                                setSelectedSlot(slotKey);
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    appointment_time: slotKey,
                                                                }));
                                                            }
                                                        }}
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 600,
                                                            fontSize: '0.85rem',
                                                            borderRadius: '10px',
                                                            height: 'auto',
                                                            py: 0.5,
                                                            px: 0.5,
                                                            minWidth: 70,
                                                            bgcolor: isSelected ? GREEN_ACCENT : (isFull ? '#f1f5f9' : '#f0fdf4'),
                                                            color: isSelected ? '#fff' : (isFull ? '#94a3b8' : '#059669'),
                                                            border: isSelected ? 'none' : '1px solid',
                                                            borderColor: isFull ? '#e2e8f0' : '#d1fae5',
                                                            '&:hover': !isFull ? {
                                                                bgcolor: isSelected ? '#059669' : '#d1fae5',
                                                            } : {},
                                                            '&.Mui-disabled': {
                                                                opacity: 0.6,
                                                                bgcolor: '#f1f5f9',
                                                                color: '#94a3b8',
                                                                border: '1px solid #e2e8f0',
                                                            },
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <TextField label="Location" fullWidth value={formData.location} onChange={handleChange('location')} sx={inputSx} />
                            <TextField label="Notes" fullWidth multiline rows={2} value={formData.notes} onChange={handleChange('notes')} sx={inputSx} />

                            {isAdmin && (
                                <FormControlLabel
                                    control={<Checkbox checked={formData.admin_override} onChange={(e) => setFormData(prev => ({ ...prev, admin_override: e.target.checked }))} sx={{ color: GREEN_ACCENT, '&.Mui-checked': { color: GREEN_ACCENT } }} />}
                                    label={<Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Override booking rules</Typography>}
                                />
                            )}
                        </Box>

                        {/* RIGHT: Availability Info Panel */}
                        {settings && (
                            <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', borderColor: '#e2e8f0' }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                                        Booking Information
                                    </Typography>

                                    {/* Slot duration */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Slot Duration</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#334155' }}>{settings.slot_duration_minutes || 60} minutes</Typography>
                                    </Box>

                                    {/* Max bookings per slot */}
                                    {(settings.max_bookings_per_slot || 1) > 1 && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Max Per Slot</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#334155' }}>{settings.max_bookings_per_slot} bookings</Typography>
                                        </Box>
                                    )}

                                    {/* Notice & max days */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Booking Window</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#334155' }}>
                                            {settings.min_notice_hours || 0}h notice, up to {settings.max_days_ahead || 60} days ahead
                                        </Typography>
                                    </Box>

                                    {/* Buffer */}
                                    {(settings.buffer_before_minutes > 0 || settings.buffer_after_minutes > 0) && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Buffer Time</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#334155' }}>
                                                {settings.buffer_before_minutes || 0}min before, {settings.buffer_after_minutes || 0}min after
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Available days */}
                                    {availDays && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Available Days</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {DAY_KEYS.map(d => {
                                                    const enabled = settings.weekly_availability?.[d]?.enabled;
                                                    return (
                                                        <Chip
                                                            key={d}
                                                            label={DAY_LABELS[d].substring(0, 3)}
                                                            size="small"
                                                            sx={{
                                                                fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24,
                                                                bgcolor: enabled ? '#d1fae5' : '#f1f5f9',
                                                                color: enabled ? '#059669' : '#94a3b8',
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Weekly hours */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Hours</Typography>
                                        {DAY_KEYS.filter(d => settings.weekly_availability?.[d]?.enabled).map(d => (
                                            <Box key={d} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#475569' }}>{DAY_LABELS[d].substring(0, 3)}</Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>
                                                    {settings.weekly_availability[d].start} - {settings.weekly_availability[d].end}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Upcoming holidays */}
                                    {upcomingHolidays.length > 0 && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Holidays</Typography>
                                            {upcomingHolidays.map((h, i) => (
                                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#ef4444' }}>{h.name}</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8' }}>
                                                        {h.recurring ? `Every ${h.date.substring(5)}` : h.date}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} sx={{ fontFamily, fontWeight: 600, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                <Button
                    variant="contained" onClick={handleSubmit}
                    disabled={submitting || !formData.title.trim() || !formData.appointment_date || !formData.appointment_time || !!dateWarning || (isAdmin && !formData.customer_user_id)}
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                    sx={{ fontFamily, fontWeight: 600, bgcolor: GREEN_ACCENT, borderRadius: '10px', textTransform: 'none', '&:hover': { bgcolor: '#059669' } }}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Appointment Card (Mobile) ---
function AppointmentCard({ appointment }) {
    return (
        <Card sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                        {appointment.title}
                    </Typography>
                    <StatusChip status={appointment.status} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                            {formatDate(appointment.appointment_date)} at {formatTime(appointment.appointment_time)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                            {appointment.duration_minutes || 30} min
                        </Typography>
                    </Box>
                    {appointment.assigned_person_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                {appointment.assigned_person_name}
                            </Typography>
                        </Box>
                    )}
                    {appointment.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                {appointment.location}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {appointment.status === 'PENDING' && appointment.onCancel && (
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined"
                            onClick={(e) => { e.stopPropagation(); appointment.onCancel(); }}
                            sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', minWidth: 'auto', px: 1.5,
                                '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' } }}>
                            Cancel Appointment
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// =============================================================================
// --- Main Page ---
// =============================================================================
export default function AppointmentsPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Detect admin/employee role
    const userStr = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    const currentUser = userStr ? JSON.parse(userStr) : null;
    const userRole = currentUser?.active_team?.user_role_in_team || 'customer';
    const isAdmin = userRole === 'admin' || userRole === 'employee';

    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [settingsConfigured, setSettingsConfigured] = useState(null); // null=loading, true/false

    // Complete/Invoice dialogs
    const [completeDialog, setCompleteDialog] = useState({ open: false, appointment: null });
    const [completionNotes, setCompletionNotes] = useState('');
    const [completingAppt, setCompletingAppt] = useState(false);
    const [invoiceDialog, setInvoiceDialog] = useState({ open: false, appointment: null });
    const [invoiceForm, setInvoiceForm] = useState({ amount: '', item_name: '', item_description: '' });
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [cancelDialog, setCancelDialog] = useState({ open: false, appointment: null });
    const [cancellingAppt, setCancellingAppt] = useState(false);

    const debouncedSearch = useDebounce(search, 400);

    // Fetch customers for admin create dialog
    useEffect(() => {
        if (!isAdmin) return;
        const fetchCustomers = async () => {
            setLoadingCustomers(true);
            try {
                const res = await axiosInstance.get('/api/users');
                const parsed = typeof res.data.data === 'string' ? JSON.parse(res.data.data) : res.data.data;
                const allUsers = parsed?.users || parsed || [];
                setCustomers(allUsers.filter(u => u.role_in_team === 'customer'));
            } catch { setCustomers([]); }
            finally { setLoadingCustomers(false); }
        };
        fetchCustomers();
    }, [isAdmin]);

    // Check if appointment settings have been configured
    useEffect(() => {
        const checkSettings = async () => {
            try {
                const res = await axiosInstance.get('/api/appointment-settings');
                const data = res.data?.data;
                setSettingsConfigured(data && data.id !== null && data.id !== undefined);
            } catch {
                setSettingsConfigured(false);
            }
        };
        checkSettings();
    }, []);

    const fetchAppointments = useCallback(async () => {
        setLoading(true);
        try {
            const statusFilter = STATUS_TABS[statusTab];
            const params = {
                page,
                per_page: rowsPerPage,
                ...(debouncedSearch && { search: debouncedSearch }),
                ...(statusFilter !== 'all' && { status: statusFilter }),
            };
            const res = await axiosInstance.get('/api/appointments', { params });
            const data = res.data;
            setAppointments(data.appointments || data.data || []);
            setTotalRows(data.total || 0);
            setTotalPages(data.total_pages || Math.ceil((data.total || 0) / rowsPerPage) || 1);
        } catch (err) {
            console.error('Error fetching appointments:', err);
            setSnackbar({ open: true, message: 'Failed to load appointments.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearch, statusTab]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    // Reset page on filter changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, statusTab]);

    const handleAppointmentSubmit = async (formData) => {
        if (isAdmin) {
            await axiosInstance.post('/api/appointments', formData);
            setSnackbar({ open: true, message: 'Appointment created successfully!', severity: 'success' });
        } else {
            await axiosInstance.post('/api/appointments/request', formData);
            setSnackbar({ open: true, message: 'Appointment request submitted successfully!', severity: 'success' });
        }
        fetchAppointments();
    };

    // Quick status change (Confirm, Activate, Cancel, No Show)
    const handleQuickStatusChange = async (appointmentId, newStatus) => {
        try {
            await axiosInstance.put(`/api/appointments/${appointmentId}`, { status: newStatus });
            const labels = { CONFIRMED: 'confirmed', IN_PROGRESS: 'started', CANCELLED: 'cancelled', NO_SHOW: 'marked as no show' };
            setSnackbar({ open: true, message: `Appointment ${labels[newStatus] || 'updated'}!`, severity: 'success' });
            fetchAppointments();
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to update appointment.', severity: 'error' });
        }
    };

    // Customer cancel with confirmation
    const handleCustomerCancelConfirm = async () => {
        if (!cancelDialog.appointment) return;
        setCancellingAppt(true);
        try {
            await axiosInstance.put(`/api/appointments/${cancelDialog.appointment.id}`, { status: 'CANCELLED' });
            setSnackbar({ open: true, message: 'Appointment cancelled successfully.', severity: 'success' });
            setCancelDialog({ open: false, appointment: null });
            fetchAppointments();
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to cancel appointment.', severity: 'error' });
        } finally {
            setCancellingAppt(false);
        }
    };

    // Mark appointment as completed with completion notes
    const handleCompleteAppointment = async () => {
        if (!completeDialog.appointment) return;
        setCompletingAppt(true);
        try {
            await axiosInstance.put(`/api/appointments/${completeDialog.appointment.id}`, {
                status: 'COMPLETED',
                completion_notes: completionNotes,
            });
            setSnackbar({ open: true, message: 'Appointment marked as done!', severity: 'success' });
            setCompleteDialog({ open: false, appointment: null });
            setCompletionNotes('');
            fetchAppointments();
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to complete appointment.', severity: 'error' });
        } finally {
            setCompletingAppt(false);
        }
    };

    // Create invoice against appointment
    const handleCreateInvoice = async () => {
        if (!invoiceDialog.appointment || !invoiceForm.amount) return;
        setCreatingInvoice(true);
        try {
            const res = await axiosInstance.post(`/api/appointments/${invoiceDialog.appointment.id}/invoice`, {
                amount: parseFloat(invoiceForm.amount),
                item_name: invoiceForm.item_name || invoiceDialog.appointment.title,
                item_description: invoiceForm.item_description || `Invoice for appointment: ${invoiceDialog.appointment.title}`,
            });
            setSnackbar({ open: true, message: 'Invoice created successfully!', severity: 'success' });
            setInvoiceDialog({ open: false, appointment: null });
            setInvoiceForm({ amount: '', item_name: '', item_description: '', payment_type: 'cash' });
            fetchAppointments();
            // Redirect to the created invoice
            const invoiceId = res.data?.data?.invoice_id || res.data?.data?.invoice?.id;
            if (invoiceId) {
                setTimeout(() => navigate(`/customer-panel/invoices/${invoiceId}`), 500);
            }
        } catch (err) {
            setSnackbar({ open: true, message: err?.response?.data?.message || 'Failed to create invoice.', severity: 'error' });
        } finally {
            setCreatingInvoice(false);
        }
    };

    const handleRowsPerPageChange = (val) => {
        setRowsPerPage(val);
        setPage(1);
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#1e293b' }}>
                        Appointments
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#64748b', mt: 0.3 }}>
                        View and manage your scheduled appointments
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        onClick={fetchAppointments}
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' },
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                        disabled={settingsConfigured === false}
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            bgcolor: GREEN_ACCENT,
                            borderRadius: '10px',
                            textTransform: 'none',
                            px: 2.5,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        {isAdmin ? 'Create Appointment' : 'Request Appointment'}
                    </Button>
                </Box>
            </Box>

            {settingsConfigured === false && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>
                    {isAdmin
                        ? 'Appointment settings have not been configured yet. Please configure them in Appointment Settings before creating appointments.'
                        : 'Appointment booking is not available yet. Please contact your administrator to enable appointments.'}
                </Alert>
            )}

            {/* Filters */}
            <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, pt: 2 }}>
                    <Tabs
                        value={statusTab}
                        onChange={(_, v) => setStatusTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 38,
                            '& .MuiTab-root': {
                                fontFamily,
                                fontWeight: 600,
                                fontSize: '0.82rem',
                                textTransform: 'none',
                                minHeight: 38,
                                px: 2,
                                color: '#64748b',
                            },
                            '& .Mui-selected': { color: GREEN_ACCENT },
                            '& .MuiTabs-indicator': { backgroundColor: GREEN_ACCENT, height: 2.5, borderRadius: 2 },
                        }}
                    >
                        {STATUS_TAB_LABELS.map((label) => (
                            <Tab key={label} label={label} />
                        ))}
                    </Tabs>
                </Box>
                <Box sx={{ px: 2.5, py: 2 }}>
                    <TextField
                        placeholder="Search by title..."
                        size="small"
                        fullWidth
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: 400,
                            '& .MuiInputBase-root': { fontFamily, borderRadius: '10px', fontSize: '0.88rem' },
                        }}
                    />
                </Box>
            </Paper>

            {/* Content */}
            <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: GREEN_ACCENT }} />
                    </Box>
                ) : appointments.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <EventIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: '#94a3b8' }}>
                            No appointments found
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#cbd5e1', mt: 0.5 }}>
                            {search ? 'Try adjusting your search or filters.' : 'Request your first appointment to get started.'}
                        </Typography>
                    </Box>
                ) : isMobile ? (
                    /* Mobile: Card Layout */
                    <Box sx={{ p: 2 }}>
                        {appointments.map((appt) => (
                            <AppointmentCard key={appt.id || appt._id} appointment={{
                                ...appt,
                                ...(!isAdmin && appt.status === 'PENDING' ? { onCancel: () => setCancelDialog({ open: true, appointment: appt }) } : {})
                            }} />
                        ))}
                    </Box>
                ) : (
                    /* Desktop: Table Layout */
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {[...(isAdmin ? ['Title', 'Customer', 'Date & Time', 'Duration', 'Assigned To', 'Location', 'Status', 'Actions'] : ['Title', 'Date & Time', 'Duration', 'Assigned To', 'Location', 'Status', 'Actions'])].map((col) => (
                                        <TableCell
                                            key={col}
                                            sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                fontSize: '0.78rem',
                                                color: '#64748b',
                                                borderBottom: '1px solid #e2e8f0',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                                py: 1.8,
                                            }}
                                        >
                                            {col}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {appointments.map((appt) => (
                                    <TableRow
                                        key={appt.id || appt._id}
                                        hover
                                        sx={{ '&:hover': { bgcolor: '#f8fafc' }, '& td': { borderBottom: '1px solid #f1f5f9' } }}
                                    >
                                        <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>
                                            {appt.title}
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {appt.customer_name || '--'}
                                                {appt.customer_type && (
                                                    <Chip label={appt.customer_type} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: appt.is_crm_customer ? '#fef3c7' : '#e0e7ff', color: appt.is_crm_customer ? '#d97706' : '#4f46e5' }} />
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {formatDate(appt.appointment_date)} at {formatTime(appt.appointment_time)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {appt.duration_minutes || 30} min
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {appt.assigned_person_name || '--'}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                            {appt.location || '--'}
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={appt.status} />
                                        </TableCell>
                                        {isAdmin ? (
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {appt.status === 'PENDING' && (
                                                        <>
                                                            <Button size="small" variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(appt.id, 'CONFIRMED'); }}
                                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#0ea5e9', color: '#0ea5e9', minWidth: 'auto', px: 1 }}>
                                                                Confirm
                                                            </Button>
                                                            <Button size="small" variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(appt.id, 'CANCELLED'); }}
                                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', minWidth: 'auto', px: 1 }}>
                                                                Cancel
                                                            </Button>
                                                        </>
                                                    )}
                                                    {appt.status === 'CONFIRMED' && (
                                                        <>
                                                            <Button size="small" variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(appt.id, 'IN_PROGRESS'); }}
                                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#10b981', color: '#10b981', minWidth: 'auto', px: 1 }}>
                                                                Start
                                                            </Button>
                                                            <Button size="small" variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(appt.id, 'CANCELLED'); }}
                                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', minWidth: 'auto', px: 1 }}>
                                                                Cancel
                                                            </Button>
                                                            <Button size="small" variant="outlined"
                                                                onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(appt.id, 'NO_SHOW'); }}
                                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#94a3b8', color: '#94a3b8', minWidth: 'auto', px: 1 }}>
                                                                No Show
                                                            </Button>
                                                        </>
                                                    )}
                                                    {appt.status === 'IN_PROGRESS' && (
                                                        <Button size="small" variant="outlined"
                                                            onClick={(e) => { e.stopPropagation(); setCompleteDialog({ open: true, appointment: appt }); setCompletionNotes(''); }}
                                                            sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#10b981', color: '#10b981', minWidth: 'auto', px: 1 }}>
                                                            Complete
                                                        </Button>
                                                    )}
                                                    {appt.status === 'COMPLETED' && (
                                                        <Button size="small" variant="outlined"
                                                            onClick={(e) => { e.stopPropagation(); setInvoiceDialog({ open: true, appointment: appt }); setInvoiceForm({ amount: '', item_name: appt.title, item_description: '' }); }}
                                                            sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#3b82f6', color: '#3b82f6', minWidth: 'auto', px: 1 }}>
                                                            Create Invoice
                                                        </Button>
                                                    )}
                                                    {appt.status === 'BILLED' && (
                                                        <Button size="small" variant="outlined"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/customer-panel/invoices/${appt.appointment_invoice_id || ''}`); }}
                                                            sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#8b5cf6', color: '#8b5cf6', minWidth: 'auto', px: 1 }}>
                                                            View Invoice
                                                        </Button>
                                                    )}
                                                    {appt.status === 'CLOSED' && appt.appointment_invoice_id && (
                                                        <Button size="small" variant="outlined"
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/customer-panel/invoices/${appt.appointment_invoice_id}`); }}
                                                            sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#059669', color: '#059669', minWidth: 'auto', px: 1 }}>
                                                            View Invoice
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        ) : (
                                            <TableCell>
                                                {appt.status === 'PENDING' && (
                                                    <Button size="small" variant="outlined"
                                                        onClick={(e) => { e.stopPropagation(); setCancelDialog({ open: true, appointment: appt }); }}
                                                        sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: '#ef4444', color: '#ef4444', minWidth: 'auto', px: 1.5,
                                                            '&:hover': { bgcolor: '#fef2f2', borderColor: '#ef4444' } }}>
                                                        Cancel
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {!loading && appointments.length > 0 && (
                    <Box sx={{ px: 2.5, pb: 2 }}>
                        <PaginationBar
                            currentPage={page}
                            totalPages={totalPages}
                            totalRows={totalRows}
                            rowsPerPage={rowsPerPage}
                            onPageChange={setPage}
                            onRowsPerPageChange={handleRowsPerPageChange}
                        />
                    </Box>
                )}
            </Paper>

            {/* Appointment Form Dialog */}
            <AppointmentFormDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleAppointmentSubmit}
                mode={isAdmin ? 'admin' : 'request'}
                customers={customers}
                loadingCustomers={loadingCustomers}
            />

            {/* Customer Cancel Confirmation Dialog */}
            <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, appointment: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700, color: '#ef4444' }}>Cancel Appointment</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mb: 1 }}>
                        Are you sure you want to cancel this appointment?
                    </Typography>
                    {cancelDialog.appointment && (
                        <Box sx={{ bgcolor: '#fef2f2', p: 2, borderRadius: '10px', mt: 1.5 }}>
                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                                {cancelDialog.appointment.title}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>
                                {cancelDialog.appointment.appointment_date} at {cancelDialog.appointment.appointment_time}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCancelDialog({ open: false, appointment: null })}
                        disabled={cancellingAppt}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b', borderRadius: '8px' }}>
                        No, Keep It
                    </Button>
                    <Button variant="contained" onClick={handleCustomerCancelConfirm}
                        disabled={cancellingAppt}
                        startIcon={cancellingAppt ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '8px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>
                        {cancellingAppt ? 'Cancelling...' : 'Yes, Cancel Appointment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Complete Appointment Dialog */}
            <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, appointment: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Complete Appointment</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mb: 2 }}>
                        Completing: <strong>{completeDialog.appointment?.title}</strong>
                    </Typography>
                    <TextField
                        label="Completion Notes"
                        fullWidth multiline rows={4}
                        value={completionNotes}
                        onChange={(e) => setCompletionNotes(e.target.value)}
                        placeholder="Add notes about what was done, outcomes, follow-ups..."
                        sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCompleteDialog({ open: false, appointment: null })} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCompleteAppointment} variant="contained" disabled={completingAppt}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: '#059669' }, px: 3 }}>
                        {completingAppt ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Mark as Completed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Invoice Dialog */}
            <Dialog open={invoiceDialog.open} onClose={() => setInvoiceDialog({ open: false, appointment: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Create Invoice</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', mb: 2 }}>
                        For appointment: <strong>{invoiceDialog.appointment?.title}</strong>
                        {invoiceDialog.appointment?.customer_name && ` — ${invoiceDialog.appointment.customer_name}`}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label="Amount *" type="number" fullWidth
                            value={invoiceForm.amount}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, amount: e.target.value }))}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Item Name" fullWidth
                            value={invoiceForm.item_name}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, item_name: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                        <TextField
                            label="Description" fullWidth multiline rows={2}
                            value={invoiceForm.item_description}
                            onChange={(e) => setInvoiceForm(prev => ({ ...prev, item_description: e.target.value }))}
                            sx={{ '& .MuiInputBase-root': { fontFamily, borderRadius: '10px' }, '& .MuiInputLabel-root': { fontFamily } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setInvoiceDialog({ open: false, appointment: null })} sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleCreateInvoice} variant="contained" disabled={creatingInvoice || !invoiceForm.amount}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, px: 3 }}>
                        {creatingInvoice ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '10px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
